import { addDays, format } from 'date-fns';
import { useState, useMemo, useCallback } from 'react';
import _cloneDeep from 'lodash/cloneDeep';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _isEqual from 'lodash/isEqual';

import { get, isEmpty } from 'ravenjs/utils/lodash';
import { addSchemaValue } from 'ravenjs/utils/schema';
import { formatDate } from 'ravenjs/utils/date';

import { PREPPED_ANSWER_UPDATES } from 'constants/tickets';
import MESSAGES from 'constants/messages';
import { MODALS } from 'constants/modals';
import { CaseCaveFieldLocalStorage } from 'utils/caseCaveFieldLocalStorage';
import { asyncDebounce } from 'utils/debounce';
import { generateSortString } from 'utils/casecave';

export function useExpiredPreppedAnswer({
    cellTypes,
    currentUser,
    currentUserId,
    fetchPreppedAnswers,
    fetchUsers,
    isLoading,
    openModal,
    schema,
    userRoleIds,
}) {
    const localStorage = useMemo(() => {
        return new CaseCaveFieldLocalStorage(
            currentUserId?.toString(),
            PREPPED_ANSWER_UPDATES.EXPIRED_PA.LOCAL_STORAGE_KEYS.ID,
            PREPPED_ANSWER_UPDATES.EXPIRED_PA.LOCAL_STORAGE_KEYS.FIELD_KEY
        );
    }, [currentUserId]);

    const defaultFilters = useMemo(() => {
        const defaultFiltersStored = localStorage.get() || {
            'filter[only_expired]': format(addDays(new Date(), 30), 'MM/DD/YYYY'),
        };

        _forEach(defaultFiltersStored, (value, key) => {
            if (key === 'filter[only_expired]') {
                defaultFiltersStored[key] = new Date(value);
            }
        });

        if (
            defaultFiltersStored &&
            !defaultFiltersStored['filter[user_id]'] &&
            !isEmpty(currentUser.info)
        ) {
            const firstName = get(currentUser, 'info.firstName', '');
            const lastName = get(currentUser, 'info.lastName', '');

            defaultFiltersStored['filter[user_id]'] = [
                {
                    label: `${firstName} ${lastName || ''}`,
                    value: currentUserId.toString(),
                },
            ];
        }

        return defaultFiltersStored;
    }, [currentUser, currentUserId, localStorage]);

    const [error, setError] = useState('');
    const [params, setParams] = useState({
        'page[number]': 1,
        'page[size]': 10,
        'stats[total]': 'count',
        sort: 'expiration_date',
    });
    const [appliedFilters, setAppliedFilters] = useState({ ...defaultFilters });
    const [listData, setListData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const listSchema = useMemo(() => {
        const listSchemaCopy = _cloneDeep(schema);

        addSchemaValue(listSchemaCopy, 'pagination.totalRows', Number(totalRecords));
        addSchemaValue(listSchemaCopy, 'list.cellTypes', cellTypes);

        return listSchemaCopy;
    }, [cellTypes, schema, totalRecords]);

    async function getAllPreppedAnswerUsers(searchSpec, callback) {
        try {
            const response = await fetchUsersList(searchSpec);
            const users = get(response, 'users', []);
            const paUpdateUser = users.map(({ firstName, lastName, userId }) => ({
                label: `${firstName} ${lastName || ''}`,
                value: userId.toString(),
            }));

            callback(paUpdateUser);

            return paUpdateUser;
        } catch (e) {
            return e;
        }
    }

    async function fetchUsersList(searchSpec = '') {
        return fetchUsers({
            limit: 10,
            roleId: userRoleIds,
            searchSpec,
            sort: 'firstName',
        });
    }

    const fetchData = useCallback(
        async (currentAppliedFilters, currentParams, isPageChange = false) => {
            const paAuthorIds = get(currentAppliedFilters, 'filter[user_id]', []);

            try {
                isLoading(true);
                const suggestedPAEditsResponse = await fetchPreppedAnswers({
                    ...currentParams,
                    ...currentAppliedFilters,
                    'filter[user_id]': paAuthorIds.map(filterOption =>
                        filterOption.value.toString()
                    ),
                });
                const suggestedPAEditsData = get(suggestedPAEditsResponse, 'data', []);

                const totalRecords = get(suggestedPAEditsResponse, 'meta.stats.total.count', 0);

                const userIdSet = new Set();

                suggestedPAEditsData.forEach(({ attributes }) => {
                    userIdSet.add(attributes.user_id);
                });

                const usersResponse = await fetchUsers({ userId: [...userIdSet] });
                const users = get(usersResponse, 'users', []);
                const usersMap = {};
                users.forEach(user => (usersMap[user.userId] = user.fullName));
                const listData = suggestedPAEditsData.map(({ attributes, id }) => {
                    return {
                        author: usersMap[attributes.user_id],
                        id,
                        preppedAnswerId: id,
                        title: get(attributes, 'title'),
                        expiration_date: isEmpty(attributes.expiration_date)
                            ? ''
                            : formatDate(attributes.expiration_date),
                        shared: _get(attributes, 'shared', true),
                        vpa: get(attributes, 'current_state'),
                    };
                });

                setListData(listData);
                if (!isPageChange) {
                    setTotalRecords(totalRecords);
                }
            } catch (error) {
                setError(error);
            } finally {
                isLoading(false);
            }
        },
        [fetchPreppedAnswers, fetchUsers, isLoading]
    );

    const fetchInitialData = useCallback(() => {
        fetchData(appliedFilters, params);
    }, [appliedFilters, fetchData, params]);

    const onChange = useCallback(
        options => {
            const { page, orderBy, order } = options;
            const sort = generateSortString(orderBy, order);

            const newParams = {
                ...params,
                'page[number]': page,
                sort,
            };

            Object.keys(newParams).forEach(
                param => newParams[param] === '' && delete newParams[param]
            );

            if (!_isEqual(params, newParams)) {
                setParams(newParams);
                fetchData(appliedFilters, newParams);
            }
        },
        [appliedFilters, fetchData, params]
    );

    const onApplyFilter = useCallback(
        filters => {
            const nextAppliedFilters = { ...filters };

            const createdStartDate = get(nextAppliedFilters, 'filter[created_at][gte]', null);
            const createdEndDate = get(nextAppliedFilters, 'filter[created_at][lte]', null);

            if (createdEndDate) createdEndDate.setHours(23, 59, 59, 999);

            if (createdStartDate && createdEndDate && createdStartDate > createdEndDate) {
                openModal(MODALS.CONFIRM, {
                    title: MESSAGES.MODAL.CONFIRM.TITLE.ERROR,
                    description: MESSAGES.MODAL.DATE_VALIDATION_MESSAGE,
                });
            } else {
                setAppliedFilters(nextAppliedFilters);
                localStorage.set(nextAppliedFilters);
                fetchData(nextAppliedFilters, params);
            }
        },
        [fetchData, localStorage, openModal, params]
    );

    const getPAUpdateUserDebounced = asyncDebounce(getAllPreppedAnswerUsers, 500);

    const getActions = useCallback(() => {
        return {
            getPAUpdateUser: getPAUpdateUserDebounced,
            handleOnChange: onChange,
            onApplyFilter,
        };
    }, [getPAUpdateUserDebounced, onApplyFilter, onChange]);

    return {
        listData,
        listSchema,
        getActions,
        fetchInitialData,
        appliedFilters,
        error,
    };
}
